import { observable, computed, action } from 'mobx';
import { ProductAddedUseCase } from '../use-cases/ProductAddedUseCase';
import { AddToBagErrors } from '../constants/AddToBagErrors';
import { ProductSelectedUseCase } from '../use-cases/ProductSelectedUseCase';
import { on, events, emit } from 'elc-service-bus';
import { ProductBaseController } from './ProductBaseController';
import { ProductGridService } from '../domain/services/ProductGridService';
import { ProductRepository } from '../repositories/ProductRepository';
import { CtaTypes } from '../constants/CTATypes';
import { InventoryStatus } from '../constants/InventoryStatuses';
import { DateFormatter } from 'elc-formatters';
import { IProductCTAConfig } from '../interfaces/IProductCTA';
import { preorderDatePlaceholderRegex } from '../views/product-preorder/PreorderMessage';

export class ProductCTAViewController extends ProductBaseController {
    @observable public addingToBag: boolean = false;
    // if the add to bag fails in the first instance, will persist the disabled state of the button
    @observable public disableAddToBag: boolean = false;
    @observable public isShowingSuccessLabel: boolean = false;
    @observable public productGridService: ProductGridService;
    @observable public isLoaded: boolean = false;
    private productRepository: ProductRepository;
    private config: IProductCTAConfig;
    private dateFormatter: DateFormatter;

    constructor(
        productGridService: ProductGridService,
        productRepository: ProductRepository,
        dateFormatter: DateFormatter
    ) {
        super();

        this.productGridService = productGridService;
        this.productRepository = productRepository;
        this.dateFormatter = dateFormatter;
    }

    @action
    public initialize = (config: IProductCTAConfig) => {
        const { skuId } = config;
        this.product = this.productRepository.getProductBySkuId(skuId);

        this.setSelectedSkuId(config.skuId);
        this.config = config;
    };

    @computed
    get data() {
        return {
            addingToBag: this.addingToBag,
            butttonLabelOverride: this.butttonLabelOverride,
            ctaType: this.ctaType,
            disableAddToBag: this.disableAddToBag,
            enableLimitedRemaining: this.enableLimitedRemaining,
            isShowingSuccessLabel: this.isShowingSuccessLabel,
            selectedSku: this.selectedSku
        };
    }

    @computed
    get enableLimitedRemaining() {
        const { features } = this.config;
        const { highVelocityQuantity = 0 } = this.selectedSku;

        return (
            features &&
            features.limitedRemaining &&
            features.limitedRemaining.enabled &&
            highVelocityQuantity > 0
        );
    }

    public getStickyButtonLabel = (buttonLabel: string) => {
        const { isDisplayedOnSticky, showPriceOnStickyAddToBag } = this.config;
        let stickyButtonLabel = buttonLabel;

        if (isDisplayedOnSticky && showPriceOnStickyAddToBag && !!this.selectedSku.priceObject) {
            stickyButtonLabel = `${buttonLabel} - ${this.selectedSku.priceObject.priceFormatted}`;
        }

        return stickyButtonLabel;
    };

    @action
    public addToBag = async (
        skuId: string,
        quantity?: number,
        showSuccessLabel?: boolean,
        replenishment?: number
    ) => {
        this.addingToBag = true;

        try {
            const addToBagUseCase = new ProductAddedUseCase(skuId, quantity, replenishment);
            await addToBagUseCase.execute();

            if (showSuccessLabel) {
                this.isShowingSuccessLabel = true;
            }
        } catch (error) {
            if (error.message === AddToBagErrors.ITEM_QTY_MAXIMUM_REACHED) {
                this.disableAddToBag = true;
            }
        }

        this.addingToBag = false;
    };

    public preorderAvailabilityLabel = (preorderMessage: string) => {
        const { preorderDateFormat } = this.config;
        const { preorderDate } = this.selectedSku;

        return this.dateFormatter.formatDateWithPlaceholder({
            date: preorderDate,
            dateFormat: preorderDateFormat,
            message: preorderMessage,
            placeholderRegex: preorderDatePlaceholderRegex
        });
    };

    public navigateToProduct = async () => {
        const { skuId, parentProduct } = this.selectedSku;
        const navigateToProductUseCase = new ProductSelectedUseCase(
            parentProduct.productUrl,
            skuId,
            this.config.positionIndex as number
        );
        await navigateToProductUseCase.execute();
    };

    public addSkuToCollection = (skuId: string, quantity?: number) => {
        emit(events.ADD_SKU_TO_COLLECTION_TRIGGERED, { skuId, quantity });
    };

    get notifyMeEnabled() {
        const { enableNotifyMe, waitlist } = this.config;

        return enableNotifyMe || (waitlist && waitlist.enabled);
    }

    get enabledByStatus() {
        const { waitlist } = this.config;

        return waitlist && waitlist.enabledByStatus ? waitlist.enabledByStatus : [];
    }

    @computed
    get enableByInventoryStatus() {
        const { inventoryStatus } = this.selectedSku;

        return (
            this.enabledByStatus.includes(inventoryStatus) ||
            (this.enabledByStatus.length === 0 && inventoryStatus !== InventoryStatus.SoldOut)
        );
    }

    get butttonLabelOverride() {
        const { translationsOverwrites } = this.config;

        return translationsOverwrites && translationsOverwrites.ctaButtonLabel;
    }

    @computed
    get ctaType() {
        const { ctaType, isCoreSite } = this.config;
        const { inventoryStatus, isPreorderable, isShoppable, isPreorderActive } = this.selectedSku;
        const isProductAvailable = inventoryStatus === InventoryStatus.Active;
        const showNotifyMeButton =
            this.notifyMeEnabled && !isProductAvailable && this.enableByInventoryStatus;

        let productCTAType = !ctaType ? CtaTypes.DEFAULT : ctaType;

        if (
            productCTAType === CtaTypes.ADD_TO_COLLECTION ||
            productCTAType === CtaTypes.ADD_TO_BAG ||
            productCTAType === CtaTypes.DEFAULT ||
            typeof productCTAType === 'undefined'
        ) {
            if (!isProductAvailable && productCTAType === CtaTypes.ADD_TO_COLLECTION) {
                productCTAType = CtaTypes.ADD_TO_BAG;
            }

            if (!isCoreSite && showNotifyMeButton) {
                return CtaTypes.NOTIFY_ME;
            }

            if ((isPreorderable && !isShoppable) || isPreorderActive) {
                return CtaTypes.PREORDER;
            }

            return isCoreSite ? CtaTypes.FIND_IN_STORE : productCTAType;
        }

        return productCTAType;
    }

    @on(events.CART_FAILURE)
    public cartFailure = () => {
        this.isShowingSuccessLabel = false;
    };

    @on(events.CART_UPDATED)
    public cartUpdated = () => {
        this.isShowingSuccessLabel = false;
    };
}
